<template>
  <Loading :loading="isSectionLoading" loading-text="Loading weekly hours...">
    <div>
      <div class="flex">
        <div>
          <div class="flex items-center">
            <img src="@/assets/my-calendar/availability/repeat.svg" alt="color" />
            <div class="card-title ml-2">Weekly hours</div>
          </div>
          <div class="mt-1 text-[12px] text-[#999999]">
            Set when you are typically available for appointment
          </div>
        </div>
        <div class="ml-[232px]">
          <ResponsiveButton
            data-testid="my-calendar.weekly-hours-section.toggle-edit-btn"
            :button_type="viewMode === 'view' ? 'outlined' : 'filled'"
            built_in_theme="dark"
            size="medium"
            :disabled="isSectionLoading"
            :display_name="viewMode === 'view' ? 'Edit' : 'Save'"
            @click-button="handleToggleMode"
          />
        </div>

      </div>

      <div v-for="day in weekDays" :key="day.full" :data-testid="`my-calendar.weekly-hours-section.day-row.${day.full}`" class="flex flex-col my-2.5">
        <div class="flex min-h-[48px]">
          <div class="week-icon">{{ day.label }}</div>
          <div class="flex flex-col gap-2">
            <template v-if="weeklyHours[day.full].length > 0">
              <div v-for="(slot, idx) in weeklyHours[day.full]" :key="idx" :data-testid="`my-calendar.weekly-hours-section.slot-row.${day.full}-${idx}`" class="flex items-center gap-3">
                <div class="w-[120px]" @click="enterEditModeIfView">
                  <SingleSelector
                    :data-testid="`my-calendar.weekly-hours-section.start-time-select.${day.full}-${idx}`"
                    class="time-select"
                    placeholder="Start"
                    :options="timeOptions"
                    size="medium"
                    :model-value="slot.start"
                    @update:model-value="val => updateStartTime(day.full, idx, val)"
                  />
                </div>
                <div class="divider-rect"></div>
                <div class="w-[120px] mr-8" @click="enterEditModeIfView">
                  <SingleSelector
                    :data-testid="`my-calendar.weekly-hours-section.end-time-select.${day.full}-${idx}`"
                    class="time-select"
                    placeholder="End"
                    :options="getFilteredEndOptions(slot.start)"
                    size="medium"
                    :model-value="slot.end"
                    @update:model-value="val => updateEndTime(day.full, idx, val)"
                  />
                </div>
                <div v-if="viewMode === 'edit'" class="flex gap-3">
                  <img
                    :data-testid="`my-calendar.weekly-hours-section.remove-slot-btn.${day.full}-${idx}`"
                    class="cursor-pointer"
                    src="@/assets/my-calendar/availability/close.svg"
                    alt="delete"
                    @click="handleDelete(day.full, idx)"
                  />
                  <img
                    :data-testid="`my-calendar.weekly-hours-section.add-slot-btn.${day.full}-${idx}`"
                    class="cursor-pointer"
                    src="@/assets/my-calendar/availability/add-circle.svg"
                    alt="add"
                    @click="handleAdd(day.full)"
                  />
                </div>
              </div>
            </template>
            <template v-else>
              <div class="flex items-center gap-8 mt-3">
                <div :data-testid="`my-calendar.weekly-hours-section.unavailable-text.${day.full}`" class="text-sm text-[#999999]">Unavailable</div>
                <img
                  v-if="viewMode === 'edit'"
                  :data-testid="`my-calendar.weekly-hours-section.add-first-slot-btn.${day.full}`"
                  class="cursor-pointer"
                  src="@/assets/my-calendar/availability/add-circle.svg"
                  alt="add"
                  @click="handleAdd(day.full)"
                />
              </div>
            </template>
          </div>
        </div>
      </div>
    </div>
  </Loading>
</template>

<script setup>
import { computed, ref } from 'vue';
import Loading from '@/NewLibrary/Loading/Loading.vue';
import { getTimeOptions } from '@/views/my-calendar/mock-data';

const props = defineProps({
  weeklyHours: {
    type: Object,
    default: () => ({}),
  },
  viewMode: {
    type: String,
    default: 'view',
  },
  saveWeeklyHours: {
    type: Function,
    required: true,
  },
  duration: {
    type: [String, Number],
    default: 60,
  },
});

const emit = defineEmits(['update:weeklyHours']);

const weekDays = [
  { full: 'Sunday', label: 'S' },
  { full: 'Monday', label: 'M' },
  { full: 'Tuesday', label: 'T' },
  { full: 'Wednesday', label: 'W' },
  { full: 'Thursday', label: 'T' },
  { full: 'Friday', label: 'F' },
  { full: 'Saturday', label: 'S' },
];
const isSectionLoading = ref(false);
const timeOptions = computed(() => getTimeOptions(props.duration));

const localWeeklyHours = computed({
  get: () => props.weeklyHours,
  set: value => emit('update:weeklyHours', value),
});

function getFilteredEndOptions(start) {
  if (!start) return timeOptions.value;
  const startIndex = timeOptions.value.findIndex(opt => opt.value === start);
  return startIndex >= 0 ? timeOptions.value.slice(startIndex + 1) : timeOptions.value;
}

function updateStartTime(day, idx, val) {
  const nextWeeklyHours = structuredClone(localWeeklyHours.value);
  nextWeeklyHours[day][idx].start = val;
  if (nextWeeklyHours[day][idx].end && val >= nextWeeklyHours[day][idx].end) {
    nextWeeklyHours[day][idx].end = '';
  }
  localWeeklyHours.value = nextWeeklyHours;
}

function updateEndTime(day, idx, val) {
  const nextWeeklyHours = structuredClone(localWeeklyHours.value);
  nextWeeklyHours[day][idx].end = val;
  localWeeklyHours.value = nextWeeklyHours;
}

function handleDelete(day, idx) {
  const nextWeeklyHours = structuredClone(localWeeklyHours.value);
  nextWeeklyHours[day].splice(idx, 1);
  localWeeklyHours.value = nextWeeklyHours;
}

function handleAdd(day) {
  const nextWeeklyHours = structuredClone(localWeeklyHours.value);
  nextWeeklyHours[day].push({ start: '', end: '' });
  localWeeklyHours.value = nextWeeklyHours;
}

async function handleToggleMode() {
  if (props.viewMode === 'view') {
    await props.saveWeeklyHours(false);
    return;
  }

  isSectionLoading.value = true;
  try {
    await props.saveWeeklyHours(true);
  } finally {
    isSectionLoading.value = false;
  }
}

function enterEditModeIfView() {
  if (props.viewMode === 'view') {
    props.saveWeeklyHours(false);
  }
}
</script>

<style scoped>
.card-title {
  font-weight: 500;
  font-size: 18px;
  line-height: 100%;
  letter-spacing: 0%;
}

.week-icon {
  width: 24px;
  height: 24px;
  background-color: #004879;
  color: #FFFFFF;
  font-size: 14px;
  border-radius: 17px;
  padding: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 10px;
  margin-right: 32px;
}

.divider-rect {
  width: 7px;
  height: 2px;
  background: #999999;
  align-self: center;
  margin: 0 4px;
}

.time-select :deep(.el-select__wrapper) {
  height: 38px;
  background: #F2F2F2;
  border:none;
  box-shadow: none !important;
  cursor: pointer;
  padding: 6px 8px 6px 12px;
}
.time-select :deep(.el-select__selected-item.el-select__placeholder) {
  color: #333;
}
.time-select :deep(.el-select__selected-item.el-select__placeholder.is-transparent) {
  color: #666;
}
</style>
